/* eslint-disable react/prop-types */
import {
  AddressFormElement,
  AddressFormElementExecution,
  AutocompleteFormElement,
  ButtonGroupFormElement,
  ButtonGroupFormElementExecution,
  CheckboxFormElement,
  CopilotAutofillElement,
  DateFormElement,
  FieldDefinitionFormElement,
  FormElement,
  FormElementType,
  FormExecutionType,
  HeadingFormElement,
  MultiSelectionFormElement,
  NestedFormElement,
  RadioFormElement,
  SelectionFormElement,
  TaskForm,
  TextFormElement,
  TextFormElementExecution,
  TypographyFormElement,
  UploadFormElement,
} from "../../types";
import { AddressInlineDisplay } from "./address/AddressInlineDisplay";
import { AddressSideSectionDisplay } from "./address/AddressSideSectionDisplay";
import AutocompleteInlineDisplay from "./autocomplete/AutocompleteInlineDisplay";
import AutocompleteSideSectionDisplay from "./autocomplete/AutocompleteSideSectionDisplay";
import { CheckboxInlineDisplay } from "./checkbox/CheckboxInlineDisplay";
import { CopilotAutofillFormComponent } from "./copilot-autofill/CopilotAutofillFormComponent";
import CopilotAutofillSideSectionDisplay from "./copilot-autofill/CopilotAutofillSideSectionDisplay";
import { DateInlineDisplay } from "./date/DateInlineDisplay";
import { DateSideSectionDisplay } from "./date/DateSideSectionDisplay";
import { FieldDefinitionInlineDisplay } from "./field-definition/FieldDefinitionInlineDisplay";
import { FormElementChangeType } from "./FormBuilder";
import { HeadingInlineDisplay } from "./heading/HeadingInlineDisplay";
import { HeadingSideSectionDisplay } from "./heading/HeadingSideSectionDisplay";
import { MultiSelectionInlineDisplay } from "./multi-selection/MultiSelectInlineDisplay";
import { NestedFormInlineDisplay } from "./nested-form/NestedFormInlineDisplay";
import { RadioInlineDisplay } from "./radio/RadioInlineDisplay";
import { SelectionInlineDisplay } from "./selection/SelectionInlineDisplay";
import { SideSectionSelectionOptionsDisplay } from "./SideSectionSelectionOptionsDisplay";
import { TextInlineDisplay } from "./text/TextInlineDisplay";
import { TextSideSectionDisplay } from "./text/TextSideSectionDisplay";
import { UploadInlineDisplay } from "./upload/UploadInlineDisplay";
import { UploadSideSectionDisplay } from "./upload/UploadSideDisplay";
import { ButtonGroupInlineDisplay } from "./wizard/button-group/ButtonGroupInlineDisplay";
import { ButtonGroupSideSectionDisplay } from "./wizard/button-group/ButtonGroupSideSectionDisplay";
import { SummaryInlineDisplay } from "./wizard/summary/SummaryInlineDisplay";
import { TypographyInlineDisplay } from "./wizard/typography/TypographyInlineDisplay";
import { TypographySideSectionDisplay } from "./wizard/typography/TypographySideSectionDisplay";
import type { JSX } from "react";

type FormBuilderElementInlineType = {
  element: FormElement;
  executionData?: FormExecutionType;
  task: TaskForm;
};
type FormBuilderElementSideSectionType<T extends FormElement = FormElement> = {
  element: T;
  executionData?: FormExecutionType;
  onChange: (props: FormElementChangeType) => void;
};

const FormBuilderElements = new Map<
  FormElementType,
  {
    Inline: (props: FormBuilderElementInlineType) => JSX.Element;
    SideSection: (props: FormBuilderElementSideSectionType) => JSX.Element;
  }
>([
  [
    FormElementType.Heading,
    {
      Inline: (props) => (
        <HeadingInlineDisplay
          {...props}
          element={props.element as HeadingFormElement}
        />
      ),
      SideSection: (props) => (
        <HeadingSideSectionDisplay
          onChange={props.onChange}
          element={props.element as HeadingFormElement}
        />
      ),
    },
  ],
  [
    FormElementType.MultiSelection,
    {
      Inline: (props) => (
        <MultiSelectionInlineDisplay
          {...props}
          element={props.element as MultiSelectionFormElement}
        />
      ),
      SideSection: (props) => (
        <SideSectionSelectionOptionsDisplay
          {...props}
          element={props.element as MultiSelectionFormElement}
        />
      ),
    },
  ],
  [
    FormElementType.Radio,
    {
      Inline: (props) => (
        <RadioInlineDisplay
          {...props}
          element={props.element as RadioFormElement}
        />
      ),
      SideSection: (props) => (
        <SideSectionSelectionOptionsDisplay
          {...props}
          element={props.element as RadioFormElement}
        />
      ),
    },
  ],
  [
    FormElementType.Selection,
    {
      Inline: (props) => (
        <SelectionInlineDisplay
          {...props}
          element={props.element as SelectionFormElement}
        />
      ),
      SideSection: (props) => (
        <SideSectionSelectionOptionsDisplay
          {...props}
          element={props.element as SelectionFormElement}
        />
      ),
    },
  ],
  [
    FormElementType.Text,
    {
      Inline: (props) => (
        <TextInlineDisplay
          {...props}
          element={props.element as TextFormElement}
          executionData={props.executionData as TextFormElementExecution}
        />
      ),
      SideSection: (props) => (
        <TextSideSectionDisplay
          {...props}
          element={props.element as TextFormElement}
          executionData={props.executionData as TextFormElementExecution}
        />
      ),
    },
  ],
  [
    FormElementType.Date,
    {
      Inline: (props) => (
        <DateInlineDisplay
          {...props}
          element={props.element as DateFormElement}
        />
      ),
      SideSection: (props) => (
        <DateSideSectionDisplay
          {...props}
          element={props.element as DateFormElement}
        />
      ),
    },
  ],
  [
    FormElementType.Autocomplete,
    {
      Inline: (props) => (
        <AutocompleteInlineDisplay
          {...props}
          element={props.element as AutocompleteFormElement}
        />
      ),
      SideSection: (props) => (
        <AutocompleteSideSectionDisplay
          {...props}
          element={props.element as AutocompleteFormElement}
        />
      ),
    },
  ],
  [
    FormElementType.Upload,
    {
      Inline: (props) => (
        <UploadInlineDisplay
          {...props}
          element={props.element as UploadFormElement}
        />
      ),
      SideSection: (props) => (
        <UploadSideSectionDisplay
          {...props}
          element={props.element as UploadFormElement}
        />
      ),
    },
  ],
  [
    FormElementType.NestedForm,
    {
      Inline: (props) => (
        <NestedFormInlineDisplay
          {...props}
          element={props.element as NestedFormElement}
        />
      ),
      SideSection: () => <></>,
    },
  ],
  [
    FormElementType.Checkbox,
    {
      Inline: (props) => (
        <CheckboxInlineDisplay
          {...props}
          element={props.element as CheckboxFormElement}
        />
      ),
      SideSection: () => <></>,
    },
  ],
  [
    FormElementType.Address,
    {
      Inline: (props) => (
        <AddressInlineDisplay
          {...props}
          element={props.element as AddressFormElement}
          executionData={props.executionData as AddressFormElementExecution}
        />
      ),
      SideSection: (props) => (
        <AddressSideSectionDisplay
          {...props}
          element={props.element as AddressFormElement}
          executionData={props.executionData as AddressFormElementExecution}
        />
      ),
    },
  ],
  [
    FormElementType.CopilotAutofill,
    {
      Inline: (props) => (
        <CopilotAutofillFormComponent
          id={props.element.id}
          name={props.element.name}
          label={props.element.label}
          disabled
        />
      ),
      SideSection: (props) => (
        <CopilotAutofillSideSectionDisplay
          {...props}
          element={props.element as CopilotAutofillElement}
        />
      ),
    },
  ],
  [
    FormElementType.FieldDefinition,
    {
      Inline: (props) => (
        <FieldDefinitionInlineDisplay
          {...props}
          element={props.element as FieldDefinitionFormElement}
        />
      ),
      SideSection: () => <></>,
    },
  ],
  // ------------------------
  //     WIZARD ELEMENTS
  // ------------------------
  [
    FormElementType.ButtonGroup,
    {
      Inline: (props) => (
        <ButtonGroupInlineDisplay
          {...props}
          element={props.element as ButtonGroupFormElement}
          executionData={props.executionData as ButtonGroupFormElementExecution}
        />
      ),
      SideSection: (props) => (
        <ButtonGroupSideSectionDisplay
          {...props}
          element={props.element as ButtonGroupFormElement}
          executionData={props.executionData as ButtonGroupFormElementExecution}
        />
      ),
    },
  ],
  [
    FormElementType.Typography,
    {
      Inline: (props) => (
        <TypographyInlineDisplay
          element={props.element as TypographyFormElement}
        />
      ),
      SideSection: (props) => (
        <TypographySideSectionDisplay
          {...props}
          element={props.element as TypographyFormElement}
        />
      ),
    },
  ],
  [
    FormElementType.Summary,
    {
      Inline: () => <SummaryInlineDisplay />,
      SideSection: () => <></>,
    },
  ],
]);

export type FormBuilderElementVariant = "inline" | "side-section";

type FormBuilderElementType<V extends FormBuilderElementVariant> = {
  variant: V;
} & (V extends "inline"
  ? FormBuilderElementInlineType
  : V extends "side-section"
    ? FormBuilderElementSideSectionType
    : never);

const FormBuilderElement = <V extends FormBuilderElementVariant>({
  variant,
  ...props
}: FormBuilderElementType<V>) => {
  const FormBuilderElementRenderer = props.element.type
    ? FormBuilderElements.get(props.element.type)
    : undefined;
  if (!FormBuilderElementRenderer) {
    return <></>;
  }
  switch (variant) {
    case "inline":
      return (
        <FormBuilderElementRenderer.Inline
          {...(props as unknown as FormBuilderElementInlineType)}
        />
      );
    case "side-section":
      return (
        <FormBuilderElementRenderer.SideSection
          {...(props as unknown as FormBuilderElementSideSectionType)}
        />
      );
  }
  return <></>;
};

export default FormBuilderElement;
